import React, { useState, useEffect } from 'react';
import { X, CreditCard, Building, ExternalLink, Wallet, Info, CheckCircle } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';
import Modal from './ui/Modal';

const InvoicePaymentLinkModal = ({ 
  onClose, 
  onSuccess,
  invoice
}) => {
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [useWallet, setUseWallet] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  // const [paymentOptions, setPaymentOptions] = useState(null);

  // Helper function to format date to British format (DD-MM-YYYY)
  const formatDateToBritish = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    loadPaymentOptions();
  }, []);

  const loadPaymentOptions = async () => {
    try {
      const walletResponse = await ClientAuthService.getWalletBalance();

      // console.log('Wallet balance response:', walletResponse);
      setWalletBalance(parseFloat(walletResponse.data.balance || 0));
      // setPaymentOptions(optionsResponse.data);
    } catch (error) {
      console.error('Failed to load payment options:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setProcessing(true);
    setError('');

    try {
      if (remainingAfterWallet > 0 && paymentMethod === 'gocardless') {
        // Check if user has GoCardless mandates
        const mandates = await ClientAuthService.getMandates();
        if (!mandates.data || mandates.data.length === 0) {
          // Redirect to mandate setup
          const successUrl = `${window.location.origin}/dashboard?tab=payments`;
          // POST to create mandate setup session
          const response = await ClientAuthService.createMandateSetup(successUrl, 'gocardless');
          // Response: {
          //   redirect_flow_id: 'RE01K60VGHJREQ5SD9Z3QZNZC9270Q7J',
          //   redirect_url: 'https://pay.gocardless.com/billing/static/flow?id=BRF01K60VGHGQ0BH2NGZTNTRKV0XK88G',
          //   session_token: 'eff308908e2947ef4f9ea0af7739312fb500beb5096b67449dd761076323f04c_68c9712bc4e435ec09d6ee6c'
          // }
          // console.log('GoCardless mandate setup response:', response);
          // return;
          
          if (response.data && response.data.redirect_url) {
            // Store session token and redirect flow id if needed (e.g., in localStorage)
            localStorage.setItem('gocardless_session_token', response.data.session_token);
            localStorage.setItem('gocardless_redirect_flow_id', response.data.redirect_flow_id);
            // Redirect user to GoCardless to complete mandate setup
            window.location.href = response.data.redirect_url;
          } else {
            throw new Error('Failed to initiate Direct Debit mandate setup');
          }
          // window.location.href = `${window.location.origin}/api/client-mandates.php?action=setup&success_url=${encodeURIComponent(successUrl)}`;
          return;
        }
        
        // Use the default or first mandate
        const defaultMandate = mandates.data.find(m => m.is_default_for_autopay) || mandates.data[0];
        
        const response = await ClientAuthService.payInvoice({
          invoice_id: invoice._id,
          payment_method: paymentMethod,
          mandate_id: defaultMandate.provider_method_id,
          use_payment_link: false, // GoCardless doesn't use payment links
          use_wallet: useWallet
        });

        if (response.data.payment_id) {
          // GoCardless payment submitted successfully
          setPaymentLink({
            payment_method: 'gocardless',
            payment_id: response.data.payment_id,
            charge_date: response.data.charge_date,
            amount: response.data.amount,
            message: 'Direct Debit payment submitted successfully'
          });
        } else if (response.data.payment_method === 'wallet') {
          // Payment completed from wallet
          onSuccess();
          onClose();
        } else {
          throw new Error('Failed to process payment');
        }
      } else {
        const response = await ClientAuthService.payInvoice({
          invoiceId: invoice._id,
          paymentMethod: paymentMethod,
          usePaymentLink: true,
          useWallet: useWallet
        });

        if (response.data.payment_link_url) {
          setPaymentLink(response.data);
        } else if (response.data.payment_method === 'wallet') {
          // Payment completed from wallet
          onSuccess();
          onClose();
        } else {
          throw new Error('Failed to create payment link');
        }
      }
    } catch (error) {
      setError(error.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const openPaymentLink = () => {
    if (paymentLink?.payment_link_url) {
      window.open(paymentLink.payment_link_url, '_blank');
      onClose();
    }
  };

  const amountDue = parseFloat(invoice.totalAmount || 0) - parseFloat(invoice.totalPaid || 0);
  const canPayFromWallet = walletBalance >= amountDue;
  const walletPaymentAmount = Math.min(walletBalance, amountDue);
  const remainingAfterWallet = useWallet ? Math.max(0, amountDue - walletBalance) : amountDue;

  // Get appropriate button text based on payment method and wallet usage
  const getButtonText = () => {
    if (processing) {
      return 'Processing Payment...';
    }
    
    if (canPayFromWallet && useWallet) {
      return 'Pay from Wallet';
    }
    
    return paymentMethod === 'gocardless' ? 'Submit Payment' : 'Create Payment Link';
  };

  const getButtonIcon = () => {
    if (canPayFromWallet && useWallet) {
      return <Wallet size={16} className="mr-2" />;
    }
    
    return paymentMethod === 'gocardless' ? 
      <CheckCircle size={16} className="mr-2" /> : 
      <ExternalLink size={16} className="mr-2" />;
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <>
          <CreditCard className="mr-2" size={20} />
          Pay Invoice {invoice.number}
        </>
      }
      size="default"
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {paymentLink ? (
        <div className="text-center">
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-4 rounded-lg mb-4">
            {paymentLink.payment_method === 'gocardless' ? (
              <>
                <h6 className="text-lg font-semibold mb-3">Direct Debit Payment Submitted</h6>
                <p className="mb-3">
                  Your Direct Debit payment has been submitted successfully.
                </p>
                <div className="text-left bg-white rounded-lg p-4 mb-4">
                  <p className="font-semibold mb-2">Payment Details:</p>
                  <ul className="space-y-1 text-sm">
                    <li>Payment ID: {paymentLink.paymentId}</li>
                    <li>Amount: £{paymentLink.amount?.toFixed(2)}</li>
                    <li>Collection Date: {formatDateToBritish(paymentLink.chargeDate) || 'Within 3-5 business days'}</li>
                  </ul>
                  <div className="flex items-start mt-3 p-3 bg-blue-50 rounded-lg">
                    <Info size={16} className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      Direct Debit payments are typically collected from your bank account within 3-5 business days.
                    </p>
                  </div>
                </div>
                <button 
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={onClose}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <h6 className="text-lg font-semibold mb-3">Payment Link Created</h6>
                <p className="mb-4">
                  Click the button below to open the secure payment page where you can complete your payment.
                </p>
                <button 
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-lg font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={openPaymentLink}
                >
                  <ExternalLink className="mr-2" size={20} />
                  Open Payment Page
                </button>
              </>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
            <div className="text-sm space-y-1">
              <div><span className="font-medium">Invoice:</span> {invoice.number}</div>
              <div><span className="font-medium">Amount:</span> £{remainingAfterWallet.toFixed(2)}</div>
              <div><span className="font-medium">Payment Method:</span> {paymentMethod === 'stripe' ? 'Card Payment' : 'Direct Debit'}</div>
              {useWallet && walletPaymentAmount > 0 && (
                <div><span className="font-medium">Wallet Payment:</span> £{walletPaymentAmount.toFixed(2)} (processed automatically)</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Invoice Details */}
          <div className="mb-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h6 className="font-semibold text-gray-900 mb-3">Invoice Details</h6>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Invoice Number</div>
                  <div className="font-medium text-gray-900">{invoice.number}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Date</div>
                  <div className="font-medium text-gray-900">{invoice.date}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Total Amount</div>
                  <div className="font-medium text-gray-900">£{parseFloat(invoice.totalAmount || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Amount Due</div>
                  <div className="font-semibold text-blue-600">£{amountDue.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Payment Option */}
          {walletBalance > 0 && (
            <div className="mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <input
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    type="checkbox"
                    id="useWallet"
                    checked={useWallet}
                    onChange={(e) => setUseWallet(e.target.checked)}
                  />
                  <label className="ml-3 flex items-center text-sm font-medium text-gray-900" htmlFor="useWallet">
                    <Wallet className="mr-2" size={16} />
                    Use Wallet Balance
                  </label>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <div>Available: £{walletBalance.toFixed(2)}</div>
                  {useWallet && (
                    <div className="mt-2">
                      {canPayFromWallet ? (
                        <div className="text-green-700 font-medium">
                          Full payment from wallet: £{amountDue.toFixed(2)}
                        </div>
                      ) : (
                        <div className="text-blue-700">
                          <div className="font-medium">Partial payment from wallet: £{walletPaymentAmount.toFixed(2)}</div>
                          <div>Remaining: £{remainingAfterWallet.toFixed(2)}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selection (only if remaining amount after wallet) */}
          {remainingAfterWallet > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Payment Method for Remaining Amount</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center">
                    <input
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      type="radio"
                      name="paymentMethod"
                      id="stripe"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <label className="ml-3 flex items-center text-sm font-medium text-gray-900" htmlFor="stripe">
                      <CreditCard className="mr-2" size={16} />
                      Card Payment
                    </label>
                  </div>
                </div>
                {/* <div>
                  <div className="flex items-center">
                    <input
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      type="radio"
                      name="paymentMethod"
                      id="gocardless"
                      value="gocardless"
                      checked={paymentMethod === 'gocardless'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <label className="ml-3 flex items-center text-sm font-medium text-gray-900" htmlFor="gocardless">
                      <Building className="mr-2" size={16} />
                      <div>
                        <div>Direct Debit</div>
                        <div className="text-xs text-gray-500">3 working days</div>
                      </div>
                    </label>
                  </div>
                </div>
              */}
              </div>
            </div>
          )}

          {/* Payment Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" size={16} />
              <div>
                <div className="font-medium text-blue-800 mb-2">Payment Summary:</div>
                <ul className="space-y-1 text-sm text-blue-700">
                  {useWallet && walletPaymentAmount > 0 && (
                    <li>Wallet payment: £{walletPaymentAmount.toFixed(2)} (instant)</li>
                  )}
                  {remainingAfterWallet > 0 && (
                    <li>
                      {paymentMethod === 'stripe' ? 'Card' : 'Direct debit'} payment: £{remainingAfterWallet.toFixed(2)}
                      {paymentMethod === 'stripe' ? ' (instant)' : ' (3 working days)'}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-between space-x-3">
            <button 
              type="button" 
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmit}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={processing}
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {getButtonText()}
                </>
              ) : (
                <>
                  {getButtonIcon()}
                  {getButtonText()}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InvoicePaymentLinkModal;